import React, { useState } from 'react';
import { IOrderDetails } from './types';
import { last } from 'lodash-es';
import { ENVIRONMENT } from '../../../../utils/constants';
import { prependLocaleToPath } from '../../../../utils/localization';
import { formatAmount, getCurrencySymbolOnly, getOrderCountryCode } from './helpers';

const envPrefix = ENVIRONMENT === 'production' ? '' : ENVIRONMENT === 'uat' ? '.perf' : `.${ENVIRONMENT}`;

const OrderDetailsHeader = ({ order }: { order: IOrderDetails }) => {
  const [shouldShowPaymentAndShippingInfo, setShouldShowAdditionalInfo] = useState(false);
  const togglePaymentAndShippingInfo = () => {
    setShouldShowAdditionalInfo((prevValue) => !prevValue);
  };

  const handleSignIn = () =>
    window.openSignInModal ? window.openSignInModal() : (window.location.href = prependLocaleToPath('/account'));
  const isPickup = Boolean(order.shipping_method?.title?.toLowerCase().includes('pickup'));
  const orderCountryCode = getOrderCountryCode(order);

  return (
    <section className="order-header">
      <div className="order-summary">
        <div className="order-short-info">
          <p className="body">
            Order Number: <span className="body bold">{order.order_number}</span>
          </p>
          <p className="body">
            Total:{' '}
            <span className="body bold">
              {getCurrencySymbolOnly(order.pricing.total.currency_code)}
              {formatAmount(Number(order.pricing.total.amount), orderCountryCode)} {order.pricing.total.currency_code}
            </span>
          </p>
          <p className="body">
            Order Placed: <span className="body bold">{order.order_placed}</span>
          </p>
          {isPickup && order.authenticated ? (
            <div
              role="button"
              className="body-text-link payment-info-trigger mt-sm"
              onClick={togglePaymentAndShippingInfo}
            >
              View Payment Info{' '}
              <span className={`alo-chevron-${shouldShowPaymentAndShippingInfo ? 'up' : 'down'}-icon`}></span>
            </div>
          ) : isPickup ? (
            <div role="button" className="body-text-link payment-info-trigger" onClick={handleSignIn}>
              Sign In for Payment and Shipping Info
            </div>
          ) : null}
        </div>
        {isPickup ? (
          <div className="exchange-barcode-container">
            <div className="barcode-container">
              <h5>Confirmation #</h5>
              <span>{last(order.order_id?.split('/'))}</span>
              <img
                src={`https://barcode${envPrefix}.alo.technology/barcode?type=Code128&data=${last(
                  order.order_id.split('/')
                )}&format=png&returnType=direct&options=%7B%22write_text%22%3Afalse%2C%22quiet_zone%22%3A0%7D`}
                alt="Order Barcode"
              />
            </div>
          </div>
        ) : order.authenticated ? (
          <div role="button" className="body-text-link payment-info-trigger" onClick={togglePaymentAndShippingInfo}>
            View Payment and Shipping Info{' '}
            <span className={`alo-chevron-${shouldShowPaymentAndShippingInfo ? 'up' : 'down'}-icon`}></span>
          </div>
        ) : (
          <div role="button" className="body-text-link payment-info-trigger" onClick={handleSignIn}>
            Sign In for Payment and Shipping Info
          </div>
        )}
      </div>
      {shouldShowPaymentAndShippingInfo && (
        <div className="additional-info">
          <div className="total-breakdown">
            <p style={{ gap: 15 }} className="body d-flex justify-content-between">
              <span>Subtotal</span>
              <span className="text-nowrap">
                {getCurrencySymbolOnly(order.pricing.subtotal.currency_code)}
                {formatAmount(Number(order.pricing.subtotal.amount), orderCountryCode)}{' '}
                {order.pricing.subtotal.currency_code}
              </span>
            </p>
            {order.pricing.tax_lines.map((tax) => (
              <p style={{ gap: 15 }} className="body d-flex justify-content-between">
                <span>
                  Tax ({tax.title.toUpperCase()} {tax.rate}%)
                </span>
                <span className="text-nowrap">
                  {getCurrencySymbolOnly(tax.price.currency_code)}
                  {formatAmount(Number(tax.price.amount), orderCountryCode)} {tax.price.currency_code}
                </span>
              </p>
            ))}
            {order.pricing.shipping && (
              <p style={{ gap: 15 }} className="body d-flex justify-content-between">
                <span>Shipping</span>
                <span className="text-nowrap">
                  {getCurrencySymbolOnly(order.pricing.shipping.currency_code)}
                  {formatAmount(Number(order.pricing.shipping.amount), orderCountryCode)}{' '}
                  {order.pricing.shipping.currency_code}
                </span>
              </p>
            )}
            <p style={{ gap: 15 }} className="body-large d-flex justify-content-between semibold">
              <span>TOTAL</span>
              <span className="text-nowrap">
                {getCurrencySymbolOnly(order.pricing.total.currency_code)}
                {formatAmount(Number(order.pricing.total.amount), orderCountryCode)} {order.pricing.total.currency_code}
              </span>
            </p>
          </div>
          <div className="payment-shipping-info">
            <div className="payment-info">
              <p className="body-large semibold pb-m-sm">Payment Method</p>
              {order.payment.methods.map((method) => (
                <p className="body">{method.display}</p>
              ))}
              {order.payment.events.map((event) => (
                <p className="body">
                  <b>{event.message}: </b>
                  {getDate(event.occurred_at)} ({event.status})
                </p>
              ))}
            </div>
            {order.shipping_method?.title && (
              <div className="shipping-info">
                <p className="body-large semibold pb-m-sm">Shipping Method</p>
                <p className="body">{order.shipping_method.title}</p>
              </div>
            )}
          </div>
          <div className="order-addresses">
            {order.billing_address && (
              <div className="billing-address">
                <p className="body-large semibold pb-m-sm">Billing Address</p>
                <p className="body">
                  {order.billing_address.address1}
                  <br />
                  {order.billing_address.address2 && (
                    <>
                      {order.billing_address.address2}
                      <br />
                    </>
                  )}
                  {order.billing_address.city}, {order.billing_address.province} {order.billing_address.zip}
                  <br />
                  {order.billing_address.country}
                  <br />
                  {order.billing_address.phone}
                </p>
              </div>
            )}
            {order.shipping_address && (
              <div className="shipping-address">
                <p className="body-large semibold pb-m-sm">Shipping Address</p>
                <p className="body">
                  {order.shipping_address.address1}
                  <br />
                  {order.shipping_address.address2 && (
                    <>
                      {order.shipping_address.address2}
                      <br />
                    </>
                  )}
                  {order.shipping_address.city}, {order.shipping_address.province} {order.shipping_address.zip}
                  <br />
                  {order.shipping_address.country}
                  <br />
                  {order.shipping_address.phone}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default OrderDetailsHeader;

const getDate = (dateString: string) => {
  const date = new Date(dateString);
  return `${date.toDateString()} ${date.toLocaleTimeString()}`;
};
